import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  fetchReport,
  listSites,
  defaultDateRange,
  isoDate,
  GSC_SCOPE,
} from "@agencylens/connector-gsc";
import { renderGSCReport } from "@agencylens/pdf";
import { DEFAULT_AGENCY_BRAND } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import {
  loadConnectionRaw,
  readAccessToken,
  readRefreshToken,
  saveGoogleConnection,
  deleteConnection,
  isExpired,
} from "@/lib/connections.js";
import { connectGoogle, refreshGoogleToken } from "@/lib/oauth.js";
import { svgElementToPng } from "@/lib/chart-rasterize.js";
import { useApp } from "@/store/app.js";

export function GSCPanel({
  onError,
  configReady,
}: {
  onError: (msg: string | undefined) => void;
  configReady: boolean;
}) {
  const {
    gscConnection,
    gscSites,
    selectedGscSiteUrl,
    lastGscReport,
    setGscConnection,
    setGscSites,
    setSelectedGscSiteUrl,
    setLastGscReport,
  } = useApp();

  const [busy, setBusy] = useState<string | undefined>();
  const [{ startDate, endDate }, setRange] = useState(() => defaultDateRange(30));
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const rec = await loadConnectionRaw("google-search-console", "default");
      if (rec) setGscConnection(rec);
    })().catch((e) => onError(String(e)));
  }, [setGscConnection, onError]);

  useEffect(() => {
    if (!gscConnection) return;
    (async () => {
      try {
        setBusy("Loading GSC sites…");
        const token = await ensureFreshToken();
        const sites = await listSites(token);
        setGscSites(sites);
        if (!selectedGscSiteUrl && sites.length > 0) {
          setSelectedGscSiteUrl(sites[0]!.siteUrl);
        }
      } catch (e) {
        onError(`Could not list Search Console sites: ${String(e)}`);
      } finally {
        setBusy(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gscConnection?.updatedAt]);

  async function ensureFreshToken(): Promise<string> {
    const rec = await loadConnectionRaw("google-search-console", "default");
    if (!rec) throw new Error("No GSC connection");
    if (!isExpired(rec)) return readAccessToken(rec);
    const refresh = await readRefreshToken(rec);
    if (!refresh) throw new Error("Token expired and no refresh token. Reconnect Search Console.");
    const refreshed = await refreshGoogleToken(refresh);
    const newRec = await saveGoogleConnection(
      "google-search-console",
      "default",
      refreshed,
    );
    setGscConnection(newRec);
    return readAccessToken(newRec);
  }

  async function onConnect() {
    onError(undefined);
    setBusy("Opening Google sign-in…");
    try {
      const tokens = await connectGoogle({ scope: GSC_SCOPE });
      const rec = await saveGoogleConnection(
        "google-search-console",
        "default",
        tokens,
      );
      setGscConnection(rec);
    } catch (e) {
      onError(`Search Console OAuth failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDisconnect() {
    await deleteConnection("google-search-console", "default");
    setGscConnection(undefined);
    setGscSites([]);
    setSelectedGscSiteUrl(undefined);
    setLastGscReport(undefined);
  }

  async function onGenerate() {
    if (!selectedGscSiteUrl) return;
    const site = gscSites.find((s) => s.siteUrl === selectedGscSiteUrl);
    if (!site) return;
    onError(undefined);
    setBusy("Fetching Search Console data…");
    try {
      const token = await ensureFreshToken();
      const report = await fetchReport(token, site, startDate, endDate);
      setLastGscReport(report);
    } catch (e) {
      onError(`Search Console fetch failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDownloadPdf() {
    if (!lastGscReport) return;
    onError(undefined);
    setBusy("Generating PDF…");
    try {
      const svgEl = chartContainerRef.current?.querySelector<SVGSVGElement>(
        "svg.recharts-surface",
      );
      let chartPng: Uint8Array | undefined;
      if (svgEl) {
        try {
          chartPng = await svgElementToPng(svgEl, 2);
        } catch (e) {
          console.warn("Chart rasterize failed; falling back to pdf-lib chart", e);
        }
      }
      const bytes = await renderGSCReport({
        data: lastGscReport,
        agency: DEFAULT_AGENCY_BRAND,
        chartPng,
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agencylens-gsc-report-${isoDate(new Date())}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      onError(`PDF generation failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <>
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-medium">Google Search Console</h2>
        {gscConnection ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Connected
            </span>
            <span className="text-sm text-slate-500">
              {gscSites.length} {gscSites.length === 1 ? "site" : "sites"} available · expires{" "}
              {new Date(gscConnection.expiresAt).toLocaleString()}
            </span>
            <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={busy !== undefined}>
              Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={onConnect} disabled={busy !== undefined || !configReady}>
            Connect Google Search Console
          </Button>
        )}
      </section>

      {gscConnection && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium">Generate Search Console report</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Site</span>
              <select
                value={selectedGscSiteUrl ?? ""}
                onChange={(e) => setSelectedGscSiteUrl(e.target.value || undefined)}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              >
                <option value="">— select —</option>
                {gscSites.map((s) => (
                  <option key={s.siteUrl} value={s.siteUrl}>
                    {s.siteUrl} ({s.permissionLevel})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Start date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setRange({ startDate: e.target.value, endDate })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">End date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setRange({ startDate, endDate: e.target.value })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={onGenerate} disabled={busy !== undefined || !selectedGscSiteUrl}>
              Fetch data
            </Button>
            {busy && <span className="text-sm text-slate-500">{busy}</span>}
          </div>
        </section>
      )}

      {lastGscReport && (
        <>
          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="text-lg font-medium">{lastGscReport.site.siteUrl}</h2>
                <p className="text-sm text-slate-500">
                  {lastGscReport.startDate} → {lastGscReport.endDate}
                </p>
              </div>
              <Button onClick={onDownloadPdf} disabled={busy !== undefined}>
                Download GSC PDF
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard label="Clicks" value={lastGscReport.totals.clicks.toLocaleString("en-US")} />
              <StatCard label="Impressions" value={lastGscReport.totals.impressions.toLocaleString("en-US")} />
              <StatCard label="CTR" value={`${(lastGscReport.totals.ctr * 100).toFixed(1)}%`} />
              <StatCard label="Avg. position" value={lastGscReport.totals.position.toFixed(1)} />
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Clicks over time</h3>
            <div ref={chartContainerRef} style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lastGscReport.daily}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="clicks" stroke="#1d4ed8" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Top queries</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Query</th>
                    <th className="py-2 pr-4 font-medium">Clicks</th>
                    <th className="py-2 pr-4 font-medium">Impressions</th>
                    <th className="py-2 pr-4 font-medium">CTR</th>
                    <th className="py-2 pr-4 font-medium">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {lastGscReport.topQueries.map((row) => (
                    <tr key={row.query} className="border-b border-slate-100">
                      <td className="py-2 pr-4">{row.query}</td>
                      <td className="py-2 pr-4">{row.clicks.toLocaleString("en-US")}</td>
                      <td className="py-2 pr-4">{row.impressions.toLocaleString("en-US")}</td>
                      <td className="py-2 pr-4">{(row.ctr * 100).toFixed(1)}%</td>
                      <td className="py-2 pr-4">{row.position.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
